/**
 * The Labs button that pushes a week to the Skylight Calendar.
 *
 * The push takes 20-30s and runs inside its own POST, so that response arrives
 * only once the work is over. The button therefore names the job itself, stores
 * the id, and starts polling `/api/skylight/status` immediately — the POST's
 * eventual answer is one more signal, never the thing that unblocks the UI.
 *
 * That is what makes leaving the screen or reloading mid-push safe: the id is
 * in storage from the tap onward, and the next mount adopts it. Two limits on
 * that, both deliberate. It is per-device — a push started on the phone is not
 * visible from the laptop, which would need the status endpoint to answer "the
 * latest job for this uid". And for the first seconds the id names a document
 * that does not exist yet, so a 404 is only believed once the job has been seen
 * or the grace period has passed; see `pollSkylightJobOnce`.
 *
 * Everything the button *shows* is decided by `deriveButtonState`, a pure
 * function tested on its own. The most important thing it does is refuse to
 * render a green check for a push whose meal half was skipped: on an account
 * without Skylight Plus the meals are dropped silently, and a partial success
 * that looks complete is worse than a visible failure.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Feather from '@expo/vector-icons/Feather';

import { RootState } from '../../redux/store';
import { selectMealsForWeek } from '../../redux/mealPlanSlice';
import { ApiError } from '../../services/apiClient';
import {
  startSkylightPush,
  getSkylightJob,
  newSkylightJobId,
  SkylightJobDto,
} from '../../services/skylightService';
import {
  setPendingSkylightJob,
  readPendingSkylightJob,
  clearPendingSkylightJob,
  pendingSkylightJobAge,
  SKYLIGHT_JOB_CREATION_GRACE_MS,
} from '../../utils/pendingSkylightPush';
import { formatDayMonth } from '../../utils/dateLocale';
import {
  Paprika,
  PaprikaFreshness,
  PaprikaRadius,
  PaprikaSpacing,
  PaprikaTypography,
} from '../../styles/theme';

export type ButtonState =
  | { kind: 'no-list'; disabled: true }
  | { kind: 'not-planned'; week: string; disabled: true }
  | { kind: 'ready'; week: string; disabled: false }
  | { kind: 'running'; phase: NonNullable<SkylightJobDto['phase']>; done: number; total: number; disabled: true }
  | { kind: 'done'; recipes: number; meals: number; groceries: number; disabled: false }
  | {
      kind: 'done-partial';
      cause: 'skipped' | 'no-meals' | 'unresolved';
      groceries: number;
      reason: string;
      /** How many meals were dropped for want of a resolvable recipe. */
      unresolved: number;
      disabled: false;
    }
  | { kind: 'failed'; message: string; disabled: false };

export function deriveButtonState(input: {
  sourceWeekStart: string | null;
  mealCount: number;
  job: SkylightJobDto | null;
}): ButtonState {
  const { sourceWeekStart, mealCount, job } = input;

  if (job?.status === 'running') {
    return { kind: 'running', phase: job.phase ?? 'recipes', done: job.done, total: job.total, disabled: true };
  }
  if (job?.status === 'failed') {
    return { kind: 'failed', message: job.error ?? '', disabled: false };
  }
  if (job?.status === 'done' && job.summary) {
    // Ordered before the ready/blocked checks: once a push has finished, its
    // outcome is what the user needs to see, not the state of the next one.
    //
    // Two different things land on `done-partial`, and neither may render as a
    // green check. `mealsSkipped` is the meal phase failing outright. Zero
    // meals *created* without a skip is subtler and was the likelier of the
    // two: the button's own gate counts meals across every week bucket
    // (`selectMealsForWeek`), while the push reads the single bucket named by
    // `weekStart`, so a mis-bucketed week can be offered as "planned" and then
    // push nothing but groceries. A recipe whose title cannot be resolved is
    // dropped just as silently. The button cannot start a push with no meals
    // planned — `not-planned` is disabled — so zero created always means
    // nothing reached the Calendar.
    //
    // The copy says "no meals were sent", not "none found", because two of the
    // causes are the push working exactly as designed: a week holding only
    // weekday breakfasts and lunches is filtered out by `isPushableSlot`, and a
    // week of only leftovers by `isRecipeMeal`. The user still planned meals,
    // pressed send and got nothing on the wall, so amber is right either way —
    // but the label must not claim the push failed to find them.
    // A third way to be partial, and the one that actually happened: some of
    // the week's meals resolved and some did not, so the push succeeded with a
    // plausible-looking count and left days blank on the wall. `mealsCreated`
    // describes only what survived, so nothing else here can notice.
    if (job.summary.mealsSkipped || job.summary.mealsCreated === 0 || (job.summary.mealsUnresolved ?? 0) > 0) {
      const cause = job.summary.mealsSkipped
        ? 'skipped'
        : job.summary.mealsCreated === 0
          ? 'no-meals'
          : 'unresolved';
      return {
        kind: 'done-partial',
        cause,
        groceries: job.summary.groceries,
        // The server's own words when it has any. The 'unresolved' case has
        // none — nothing threw, the meals simply were not there — so its
        // sentence is built in the render layer, where `t` lives.
        reason: job.summary.skipReason ?? '',
        unresolved: job.summary.mealsUnresolved ?? 0,
        disabled: false,
      };
    }
    return { kind: 'done', recipes: job.summary.recipes, meals: job.summary.mealsCreated,
      groceries: job.summary.groceries, disabled: false };
  }
  if (!sourceWeekStart) return { kind: 'no-list', disabled: true };
  if (mealCount === 0) return { kind: 'not-planned', week: sourceWeekStart, disabled: true };
  return { kind: 'ready', week: sourceWeekStart, disabled: false };
}

const POLL_INTERVAL_MS = 1500;

/** What the button shows while it waits for the first answer about a job. */
const PENDING_JOB = (jobId: string): SkylightJobDto => ({
  jobId,
  status: 'running',
  phase: null,
  done: 0,
  total: 0,
});

export interface PollSink {
  /** The job as the server describes it right now. */
  onJob(job: SkylightJobDto): void;
  /** The job is not coming (see the rules below) — go back to ready. */
  onForgotten(): void;
}

export type PollOutcome = 'running' | 'settled' | 'forgotten' | 'unreachable';

/**
 * One poll, and the storage bookkeeping that goes with it.
 *
 * Pulled out of the effect so the resume path can be tested: this jest config
 * stubs `react-native` rather than rendering it, so there is no way to mount
 * the component and watch it.
 *
 * The three outcomes that are not 'running' each mean something different:
 *  - 'settled'     — done or failed. The stored id has served its purpose.
 *  - 'forgotten'   — the job is not coming. Clearing the id and falling back to
 *                    ready is right; a permanent error would not be.
 *  - 'unreachable' — nothing was learned. Nothing is cleared and polling goes on.
 *
 * **A 404 is not automatically 'forgotten'.** The id is minted by the client, so
 * for the first moments of a push the id exists and the document does not: the
 * first poll leaves within milliseconds of the tap, while the POST that creates
 * the document is still crossing the network. Treating that 404 as 'forgotten'
 * erases the id and stops polling *while the push runs* — the exact symptom the
 * job document exists to prevent.
 *
 * So a 404 only means forgotten once one of these holds:
 *  - `seen` — this job has already been read at least once, so its document did
 *    exist and is now gone (a deleted document, a wiped collection);
 *  - the stored entry is older than `SKYLIGHT_JOB_CREATION_GRACE_MS`, so the
 *    POST has had every chance to create it and plainly did not;
 *  - there is no stored entry at all, so there is nothing left to protect.
 *
 * Time-based rather than "wait for the POST": the resume path has no POST to
 * wait for — it adopts an id from a previous session — and `savedAt` is already
 * on the stored entry, so the grace costs no new state. `seen` is what keeps a
 * genuinely vanished job from waiting out the grace for no reason.
 */
export async function pollSkylightJobOnce(
  jobId: string,
  sink: PollSink,
  opts: { seen?: boolean } = {}
): Promise<PollOutcome> {
  try {
    const job = await getSkylightJob(jobId);
    if (job.status !== 'running') {
      await clearPendingSkylightJob();
      sink.onJob(job);
      return 'settled';
    }
    sink.onJob(job);
    return 'running';
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      const age = await pendingSkylightJobAge();
      const past = age === null || age > SKYLIGHT_JOB_CREATION_GRACE_MS;
      if (opts.seen || past) {
        await clearPendingSkylightJob();
        sink.onForgotten();
        return 'forgotten';
      }
      // Not yet, rather than never. Keep the id, keep polling.
      return 'unreachable';
    }
    return 'unreachable';
  }
}

export interface StartSink {
  /** The push has a name and a stored id — start polling it now. */
  onJobId(jobId: string): void;
  /** The server refused outright, so no job will ever exist to poll. */
  onRefused(message: string): void;
}

export type StartOutcome = 'started' | 'refused' | 'unconfirmed';

/**
 * Begin a push, and hand the id back before the POST is answered.
 *
 * Order is the whole point: the id is minted, persisted and announced FIRST,
 * and only then is the request awaited. The POST carries the push itself and
 * answers 20-30s later; anything that waited for it would show a dead button
 * for the length of the push and lose the job entirely on a reload.
 *
 * What a rejected POST means depends on who rejected it:
 *  - 4xx — the server refused before creating anything (bad week, not admin,
 *    id already in use). No job document will ever exist, polling would only
 *    404 into silence, so this is reported locally and the stored id dropped.
 *  - anything else — a 500, a killed function, a dropped connection. The job
 *    document exists and is the authority on what happened; the push may even
 *    have succeeded. Polling settles it, and the 90s staleness rule resolves a
 *    run that really died.
 */
export async function beginSkylightPush(weekStart: string, sink: StartSink): Promise<StartOutcome> {
  const jobId = newSkylightJobId();
  await setPendingSkylightJob(jobId);
  sink.onJobId(jobId);

  try {
    await startSkylightPush(weekStart, jobId);
    return 'started';
  } catch (err) {
    if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
      await clearPendingSkylightJob();
      // shown raw: an admin-only Labs card, and the server's refusal reason is
      // exactly what the admin came to read.
      sink.onRefused(err.message);
      return 'refused';
    }
    return 'unconfirmed';
  }
}

const ICONS: Record<ButtonState['kind'], React.ComponentProps<typeof Feather>['name']> = {
  'no-list': 'upload-cloud',
  'not-planned': 'upload-cloud',
  ready: 'upload-cloud',
  running: 'refresh-cw',
  done: 'check',
  'done-partial': 'alert-triangle',
  failed: 'alert-circle',
};

export const SkylightPushButton: React.FC = () => {
  const { t, i18n } = useTranslation();

  const sourceWeekStart = useSelector(
    (state: RootState) => state.groceryList?.sourceWeekStart ?? null
  );
  const mealCount = useSelector((state: RootState) =>
    sourceWeekStart ? selectMealsForWeek(sourceWeekStart)(state).length : 0
  );

  const [jobId, setJobId] = useState<string | null>(null);
  const [job, setJob] = useState<SkylightJobDto | null>(null);
  /** A push started from this mount outranks anything the resume read finds. */
  const startedHere = useRef(false);
  // `disabled` cannot close this window on its own: `handlePress` awaits the
  // storage write before `onJobId` sets state, so a second tap in the same tick
  // still sees an enabled button. Two pushes are not merely wasteful — they
  // read the same sittings, so both can create the full week and leave twice
  // the meals on a wall display, and the loser spends the shared refresh token
  // and reports `invalid_grant`, which reads as "your credentials are broken"
  // when nothing is wrong.
  const inFlight = useRef(false);

  // Adopt a push that was already in flight when this screen last went away.
  // Runs once, before anything is on screen, so there is no flash of "ready"
  // over a job that is still writing to the Calendar.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const stored = await readPendingSkylightJob();
      if (cancelled || !stored || startedHere.current) return;
      setJob(PENDING_JOB(stored));
      setJobId(stored);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;

    // Whether this job has ever come back from the server. Until it has, a 404
    // is "the POST has not created it yet", not "it is gone".
    let seen = false;

    const poll = async () => {
      const outcome = await pollSkylightJobOnce(jobId, {
        onJob: next => {
          seen = true;
          if (!cancelled) setJob(next);
        },
        onForgotten: () => {
          if (cancelled) return;
          setJob(null);
          setJobId(null);
        },
      }, { seen });
      // Stop as soon as the job settles — nothing more will change. A dropped
      // poll ('unreachable') says nothing about the job, so keep asking; the
      // 90s staleness rule server-side is what ends a push that really died.
      if (outcome === 'settled' || outcome === 'forgotten') clearInterval(timer);
    };

    const timer = setInterval(poll, POLL_INTERVAL_MS);
    void poll();

    // Also the unmount path: leaving Settings mid-push must not leave a timer
    // calling the API forever.
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [jobId]);

  const handlePress = useCallback(async () => {
    if (!sourceWeekStart) return;
    if (inFlight.current) return;
    inFlight.current = true;
    startedHere.current = true;
    // Held only until the button can defend itself. `onJobId` sets a running
    // job, which makes `state.disabled` true, so from that moment the ref has
    // nothing left to guard. Holding it for the whole 20-30s POST instead would
    // mean a connection that never settles leaves the button permanently
    // unpressable — including after the staleness rule offers a retry.
    const release = () => {
      inFlight.current = false;
    };
    try {
      await beginSkylightPush(sourceWeekStart, {
        // Running from the tap, and polling from the tap — neither waits for the
        // POST, which does not answer until the push is over.
        onJobId: id => {
          setJob(PENDING_JOB(id));
          setJobId(id);
          release();
        },
        onRefused: message => {
          setJobId(null);
          setJob({ jobId: '', status: 'failed', phase: null, done: 0, total: 0, error: message });
          release();
        },
      });
    } finally {
      release();
    }
  }, [sourceWeekStart]);

  const state = deriveButtonState({ sourceWeekStart, mealCount, job });
  const week = (iso: string) => formatDayMonth(iso, i18n.language, 'short');

  let label: string;
  switch (state.kind) {
    case 'no-list':
      label = t('settings.labs.noGroceryList');
      break;
    case 'not-planned':
      label = t('settings.labs.weekNotPlanned', { week: week(state.week) });
      break;
    case 'running': {
      const counts = { done: state.done, total: state.total };
      // Spelled out rather than built from `state.phase` so the keys stay
      // greppable for the orphan-key guard.
      if (state.phase === 'deletions') label = t('settings.labs.phaseDeletions', counts);
      else if (state.phase === 'meals') label = t('settings.labs.phaseMeals', counts);
      else if (state.phase === 'groceries') label = t('settings.labs.phaseGroceries', counts);
      else label = t('settings.labs.phaseRecipes', counts);
      break;
    }
    case 'done':
      label = t('settings.labs.done', {
        recipes: state.recipes,
        meals: state.meals,
        groceries: state.groceries,
      });
      break;
    case 'done-partial':
      if (state.cause === 'no-meals') label = t('settings.labs.doneNoMeals');
      else if (state.cause === 'unresolved')
        label = t('settings.labs.doneSomeMealsMissing', { count: state.unresolved });
      else label = t('settings.labs.doneMealsSkipped');
      break;
    case 'failed':
      label = t('settings.labs.failed');
      break;
    default:
      label = t('settings.labs.push', { week: week(state.week) });
  }

  const tone =
    state.kind === 'done'
      ? styles.buttonDone
      : state.kind === 'done-partial'
        ? styles.buttonPartial
        : state.kind === 'failed'
          ? styles.buttonFailed
          : undefined;

  const toneText =
    state.kind === 'done'
      ? styles.labelDone
      : state.kind === 'done-partial'
        ? styles.labelPartial
        : state.kind === 'failed'
          ? styles.labelFailed
          : undefined;

  const iconColor =
    state.kind === 'done'
      ? Paprika.success
      : state.kind === 'done-partial'
        ? PaprikaFreshness.urgent.text
        : state.kind === 'failed'
          ? Paprika.destructive
          : Paprika.accent;

  // Dimmed means "there is nothing to send", not "not pressable": a push in
  // flight is disabled too, but it is the liveliest thing on the screen and the
  // wireframe draws it at full strength.
  const dimmed = state.kind === 'no-list' || state.kind === 'not-planned';

  const progress =
    state.kind === 'running' && state.total > 0
      ? Math.min(1, state.done / state.total)
      : 0;

  // The line that says what actually went wrong.
  //
  // Every layer below builds a specific message — `invalid_grant` names the
  // command that fixes it, the week guard names both weeks, `resolveCategoryIds`
  // lists the categories it really saw — and all of it used to stop one line
  // short of the screen. The headline above stays generic because it is the
  // right summary for the timeout it was written for; this is where the reason
  // goes. Untranslated on purpose: it comes from the server, and inventing a
  // key per failure would mean guessing the failures in advance.
  const detail =
    state.kind === 'failed'
      ? state.message
      : state.kind === 'done-partial'
        ? state.cause === 'unresolved'
          ? t('settings.labs.unresolvedHint')
          : state.reason
        : '';

  return (
    <View>
      <TouchableOpacity
        testID="skylight-push-button"
        style={[styles.button, tone, dimmed && styles.buttonDisabled]}
        onPress={handlePress}
        disabled={state.disabled}
        accessibilityRole="button"
        accessibilityState={{ disabled: state.disabled }}
        accessibilityLabel={label}
      >
        <Feather
          name={ICONS[state.kind]}
          size={18}
          color={iconColor}
          style={{ marginRight: PaprikaSpacing.sm }}
        />
        <Text style={[styles.label, toneText]}>{label}</Text>
      </TouchableOpacity>

      {state.kind === 'running' && (
        <View style={styles.progressTrack}>
          <View style={[styles.progressFill, { width: `${progress * 100}%` }]} />
        </View>
      )}

      {detail !== '' && (
        <Text
          testID="skylight-push-detail"
          style={[styles.detail, state.kind === 'failed' ? styles.detailFailed : styles.detailPartial]}
        >
          {detail}
        </Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  // Mirrors `styles.reportButton` in settings.tsx — same terracotta outline,
  // same pill.
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: Paprika.accent,
    borderRadius: PaprikaRadius.xl,
    paddingVertical: 14,
    paddingHorizontal: PaprikaSpacing.xl,
    marginTop: PaprikaSpacing.sm,
    alignSelf: 'center',
    minWidth: Platform.OS === 'web' ? 220 : 200,
  },
  buttonDisabled: {
    opacity: 0.45,
  },
  buttonDone: {
    borderColor: Paprika.success,
  },
  // Deliberately not the green check: the meal half was skipped.
  buttonPartial: {
    borderColor: PaprikaFreshness.urgent.text,
    backgroundColor: PaprikaFreshness.urgent.bg,
  },
  buttonFailed: {
    borderColor: Paprika.destructive,
  },
  label: {
    ...PaprikaTypography.labelLarge,
    fontSize: 16,
    color: Paprika.accent,
    flexShrink: 1,
    textAlign: 'center',
  },
  labelDone: {
    color: Paprika.success,
  },
  labelPartial: {
    color: PaprikaFreshness.urgent.text,
  },
  labelFailed: {
    color: Paprika.destructive,
  },
  // Secondary to the pill, never louder than it: the headline says what
  // happened, this says why.
  detail: {
    ...PaprikaTypography.caption,
    marginTop: PaprikaSpacing.sm,
    paddingHorizontal: PaprikaSpacing.md,
    textAlign: 'center',
  },
  detailFailed: {
    color: Paprika.destructive,
  },
  detailPartial: {
    color: Paprika.textSecondary,
  },
  progressTrack: {
    height: 4,
    borderRadius: PaprikaRadius.full,
    backgroundColor: Paprika.surfaceHighest,
    marginTop: PaprikaSpacing.sm,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: PaprikaRadius.full,
    backgroundColor: Paprika.accent,
  },
});

export default SkylightPushButton;
